/**
 * DocumentJobBadge — Stage-Status-Badge fuer Pipeline v3.
 *
 * Zeigt OCR-/Title-Stage-Status mit konsistenten Farben + Icons.
 * Failed-Variante hat optional einen Retry-Button (POST /reanalyze).
 */

import React from 'react';
import { Loader2, CheckCircle, AlertTriangle, Sparkles, Clock, RefreshCw } from 'lucide-react';
import type { JobState } from '@/hooks/useDocumentJobStates';

type Props = {
  jobState?: JobState | null;
  /** Legacy-Fallback: wenn kein jobState vorhanden, nutze documents.analysis_status */
  legacyAnalysisStatus?: string;
  onRetry?: () => void;
  compact?: boolean;
};

export function DocumentJobBadge({ jobState, legacyAnalysisStatus, onRetry, compact }: Props) {
  // Wenn ein Job-State vorhanden → Pipeline v3 Anzeige, sonst Legacy-Mapping
  const state = jobState?.state || mapLegacy(legacyAnalysisStatus);

  let label = 'Fertig';
  let color = 'bg-green-100 text-green-700';
  let Icon: any = CheckCircle;
  let spin = false;
  let showRetry = false;

  switch (state) {
    case 'pending_ocr':
      label = 'In Verarbeitung';
      color = 'bg-gray-100 text-gray-700';
      Icon = Clock;
      break;
    case 'pending_title':
      label = 'Titel wird analysiert';
      color = 'bg-yellow-100 text-yellow-800';
      Icon = Sparkles;
      spin = true;
      break;
    case 'failed_ocr':
      label = 'OCR-Fehler';
      color = 'bg-red-100 text-red-700';
      Icon = AlertTriangle;
      showRetry = true;
      break;
    case 'failed_title':
      label = 'Titel-Fehler';
      color = 'bg-orange-100 text-orange-700';
      Icon = AlertTriangle;
      showRetry = true;
      break;
    case 'processing':
      label = 'Wird analysiert';
      color = 'bg-blue-100 text-blue-700';
      Icon = Loader2;
      spin = true;
      break;
    case 'done':
    default:
      // 'fertig' bei abgeschlossenem Doc — bei vielen Dokumenten visuell zu laut.
      // Wir geben dann nichts zurueck (Caller entscheidet).
      if (compact) return null;
      break;
  }

  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        <Icon className={`w-3 h-3 ${spin ? 'animate-spin' : ''}`} />
        {label}
      </span>
      {showRetry && onRetry && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRetry(); }}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
          title="Erneut analysieren"
        >
          <RefreshCw className="w-3 h-3" /> Erneut
        </button>
      )}
    </span>
  );
}

function mapLegacy(status?: string): JobState['state'] | 'processing' {
  switch (status) {
    case 'pending':    return 'pending_ocr';
    case 'processing': return 'processing' as any;
    case 'failed':     return 'failed_ocr';
    case 'completed':
    default:           return 'done';
  }
}

export default DocumentJobBadge;
